import React, { Component } from 'react';
import { flowRight as compose } from 'lodash';
import { withQuery, withMutation } from '../../../hoc/withGraphql';
import getUnsettledPayments from './getUnsettledPayments.graphql';
import chargeUnsettledPaymentM from './chargeUnsettledPayment.graphql';
import resolveUnsettledPaymentM from './resolveUnsettledPayment.graphql';

const RED = '#D32F2F';
const INK = '#1F2733';

// Admin queue for completed jobs whose final balance (extras/time/helpers/materials) couldn't be
// auto-charged. The retry cron keeps trying off-session + reminding the client and escalates here
// after repeated failures. Charge now = retry the saved card on demand; Mark settled = collected
// outside the app / waived (stops the cron chasing it).
class UnsettledPayments extends Component {
  constructor(props) {
    super(props);
    this.state = { tab: 'all', busy: false, message: '', currentPage: 1 };
  }

  refetch = () => {
    const d = this.props.data;
    if (d && d.refetch) d.refetch({ currentPage: this.state.currentPage, searchList: this.state.tab });
  };

  switchTab = (tab) => {
    this.setState({ tab, message: '', currentPage: 1 }, this.refetch);
  };

  go = (delta) => this.setState((s) => ({ currentPage: Math.max(1, s.currentPage + delta) }), this.refetch);

  age = (iso) => {
    if (!iso) return '—';
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    if (days >= 1) return `${days}d`;
    const hrs = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
    return `${hrs}h`;
  };

  charge = async (row) => {
    if (!window.confirm(`Charge ${row.currency || ''} ${Number(row.amount).toFixed(2)} now to ${row.clientName || row.userId}'s saved card for booking #${row.bookingId}?`)) return;
    this.setState({ busy: true, message: '' });
    try {
      const res = await this.props.chargeMut({ variables: { bookingId: row.bookingId } });
      const r = res && res.data && res.data.chargeUnsettledPayment;
      if (r && r.status === 200) {
        this.setState({ busy: false, message: r.message || `Booking #${row.bookingId} charged.` });
        this.refetch();
      } else {
        this.setState({ busy: false, message: (r && r.errorMessage) || 'Charge failed.' });
      }
    } catch (e) { this.setState({ busy: false, message: e.message }); }
  };

  resolve = async (row) => {
    const reason = window.prompt(`Mark booking #${row.bookingId} (${row.currency || ''} ${Number(row.amount).toFixed(2)}) as settled WITHOUT charging the card.\nReason (e.g. "collected in cash" / "waived"):`, 'collected');
    if (reason === null) return;
    this.setState({ busy: true, message: '' });
    try {
      const res = await this.props.resolveMut({ variables: { bookingId: row.bookingId, reason } });
      const r = res && res.data && res.data.resolveUnsettledPayment;
      if (r && r.status === 200) {
        this.setState({ busy: false, message: r.message || `Booking #${row.bookingId} settled.` });
        this.refetch();
      } else {
        this.setState({ busy: false, message: (r && r.errorMessage) || 'Failed.' });
      }
    } catch (e) { this.setState({ busy: false, message: e.message }); }
  };

  render() {
    const btn = (bg) => ({ background: bg, color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', marginRight: 8 });
    const tabBtn = (t) => ({ ...btn(this.state.tab === t ? RED : '#fff'), color: this.state.tab === t ? '#fff' : INK, border: this.state.tab === t ? 'none' : '1px solid #ddd' });

    const q = this.props.data && this.props.data.getUnsettledPayments;
    const rows = (q && q.results) || [];
    const alertedCount = (q && q.alertedCount) || 0;
    const totalOutstanding = (q && q.totalOutstanding) || 0;
    const cur = rows[0] && rows[0].currency ? rows[0].currency : '';
    const count = (q && q.count) || 0;
    const PAGE_SIZE = 10;
    const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

    return (
      <div style={{ fontFamily: 'Poppins, sans-serif' }}>
        <h1 className="siteAdminPrimaryHeading">Unsettled Payments</h1>
        <p style={{ color: '#777', marginTop: -8, marginBottom: 16 }}>
          Completed jobs whose final balance couldn&apos;t be collected. The system auto-retries the
          client&apos;s card every hour and reminds them; rows flagged <b>alerted</b> have failed
          repeatedly and need you. <b>Charge now</b> retries the card; <b>Mark settled</b> clears it
          (collected outside the app / waived).
        </p>

        <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
          <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: '12px 18px' }}>
            <div style={{ fontSize: 12, color: '#888' }}>Outstanding total</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: RED }}>{cur} {totalOutstanding.toFixed(2)}</div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: '12px 18px' }}>
            <div style={{ fontSize: 12, color: '#888' }}>Need attention</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: INK }}>{alertedCount}</div>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <button style={tabBtn('all')} onClick={() => this.switchTab('all')}>All</button>
          <button style={tabBtn('alerted')} onClick={() => this.switchTab('alerted')}>Need attention {alertedCount ? `(${alertedCount})` : ''}</button>
          {this.state.message ? <span style={{ marginLeft: 12, color: RED, fontWeight: 600 }}>{this.state.message}</span> : null}
        </div>

        <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead><tr style={{ background: '#fafafa', textAlign: 'left' }}>
              <th style={{ padding: 10 }}>Booking</th>
              <th style={{ padding: 10 }}>Client / Worker</th>
              <th style={{ padding: 10 }}>Amount</th>
              <th style={{ padding: 10 }}>Unpaid for</th>
              <th style={{ padding: 10 }}>Attempts</th>
              <th style={{ padding: 10 }}>Card</th>
              <th style={{ padding: 10 }}>State</th>
              <th style={{ padding: 10 }}>Actions</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={8} style={{ padding: 20 }}>No unsettled payments. 🎉</td></tr>}
              {rows.map((r) => (
                <tr key={r.bookingId} style={{ borderTop: '1px solid #f0f0f0', verticalAlign: 'top', background: r.adminAlerted ? '#FFF6F6' : '#fff' }}>
                  <td style={{ padding: 10 }}>#{r.bookingId}</td>
                  <td style={{ padding: 10 }}>{r.clientName || r.userId || '—'}<div style={{ fontSize: 12, color: '#888' }}>{r.partnerName || ''}</div></td>
                  <td style={{ padding: 10, fontWeight: 700, color: RED }}>{r.currency || ''} {Number(r.amount).toFixed(2)}</td>
                  <td style={{ padding: 10 }}>{this.age(r.unpaidSince)}</td>
                  <td style={{ padding: 10 }}>{r.settleAttempts}</td>
                  <td style={{ padding: 10 }}>{r.hasCard ? <span style={{ color: '#2E7D32', fontWeight: 600 }}>on file</span> : <span style={{ color: '#9E9E9E' }}>none</span>}</td>
                  <td style={{ padding: 10 }}>
                    {r.adminAlerted
                      ? <span style={{ padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, color: '#fff', background: RED }}>alerted</span>
                      : <span style={{ padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, color: '#fff', background: '#F9A825' }}>retrying</span>}
                  </td>
                  <td style={{ padding: 10, whiteSpace: 'nowrap' }}>
                    <button style={btn(r.hasCard ? '#2E7D32' : '#BDBDBD')} disabled={this.state.busy || !r.hasCard} onClick={() => this.charge(r)}>Charge now</button>
                    <button style={btn('#9E9E9E')} disabled={this.state.busy} onClick={() => this.resolve(r)}>Mark settled</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {count > PAGE_SIZE && (
          <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
            <button style={btn(RED)} disabled={this.state.currentPage <= 1} onClick={() => this.go(-1)}>Prev</button>
            <span>Page {this.state.currentPage} / {totalPages} ({count})</span>
            <button style={btn(RED)} disabled={this.state.currentPage >= totalPages} onClick={() => this.go(1)}>Next</button>
          </div>
        )}
      </div>
    );
  }
}

export default compose(
  withQuery(getUnsettledPayments, { name: 'data', options: { variables: { currentPage: 1, searchList: 'all' }, fetchPolicy: 'network-only', ssr: false } }),
  withMutation(chargeUnsettledPaymentM, { name: 'chargeMut' }),
  withMutation(resolveUnsettledPaymentM, { name: 'resolveMut' }),
)(UnsettledPayments);
